import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle, ArrowRight } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { api, imageUrl, whatsappLink } from "../lib/api";
import SEO, { faqSchema, breadcrumbSchema } from "../components/SEO";

export default function FAQ() {
    const { t, lang } = useLang();
    const { site } = useOutletContext() || {};
    const [openIdx, setOpenIdx] = useState(0);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/faqs")
            .then((r) => setFaqs(r.data || []))
            .catch(() => setFaqs([]))
            .finally(() => setLoading(false));
    }, []);

    // Localise: map raw docs to {q, a} for the current language, falling back to EN
    const items = faqs.map((f) => ({
        id: f.id,
        q: f[`question_${lang}`] || f.question_en || f.question_al || "",
        a: f[`answer_${lang}`] || f.answer_en || f.answer_al || "",
    })).filter((it) => it.q && it.a);

    const whatsapp = site?.whatsapp || "355682116731";

    return (
        <div data-testid="faq-page" className="bg-[#FDFBF7]">
            <SEO
                title={t("nav_faq")}
                description={lang === "al" ? "Pyetjet më të shpeshta për Kompleksin Dea Radhimë — check-in, parking, plazhi, anulimi, kafshët." : "Frequently asked questions about Kompleksi Dea Radhimë — check-in, parking, beach, cancellation, pets."}
                path="/faq"
                schemas={[
                    faqSchema(items),
                    breadcrumbSchema([
                        { name: lang === "al" ? "Kreu" : "Home", url: "/" },
                        { name: t("nav_faq"), url: "/faq" },
                    ]),
                ]}
            />

            {/* Header */}
            <section className="relative h-[40vh] min-h-[300px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${imageUrl("seed:gallery_beach")})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 pt-32 w-full">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">FAQ</span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {t("faq_title")}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {t("faq_intro")}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* FAQ list */}
            <section className="py-16 md:py-24 px-6 md:px-12 max-w-4xl mx-auto">
                {loading ? (
                    <div className="text-center py-12 text-[#1C1C1C]/50 font-light" data-testid="faq-loading">
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-12 text-[#1C1C1C]/60 font-light" data-testid="faq-empty">
                        {lang === "al" ? "Nuk ka pyetje ende." : "No FAQs yet."}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((item, i) => {
                            const isOpen = openIdx === i;
                            return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    data-testid={`faq-item-${i}`}
                                    className={`bg-white rounded-2xl border transition-all duration-300 ${
                                        isOpen ? "border-[#0F3A59]/40 shadow-[0_10px_30px_rgb(0,0,0,0.06)]" : "border-[#E5E0D8]"
                                    }`}
                                >
                                    <button
                                        onClick={() => setOpenIdx(isOpen ? -1 : i)}
                                        className="w-full text-left px-5 md:px-7 py-5 md:py-6 flex items-center justify-between gap-4"
                                        data-testid={`faq-toggle-${i}`}
                                    >
                                        <span className="font-serif text-lg md:text-xl text-[#0F3A59] leading-snug">
                                            {item.q}
                                        </span>
                                        <motion.span
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="shrink-0 w-9 h-9 rounded-full bg-[#F4EFE6] text-[#0F3A59] flex items-center justify-center"
                                        >
                                            <ChevronDown size={18} strokeWidth={1.8} />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-5 md:px-7 pb-6 text-[#1C1C1C]/75 font-light leading-relaxed whitespace-pre-line">
                                                    {item.a}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* CTA at the bottom */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-16 bg-[#0F3A59] rounded-2xl p-8 md:p-10 text-center text-white"
                >
                    <h3 className="font-serif text-2xl md:text-3xl">
                        {t("faq_more_questions")}
                    </h3>
                    <p className="text-white/75 font-light mt-3 max-w-xl mx-auto">
                        {t("faq_more_questions_sub")}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
                        <a
                            href={whatsappLink(whatsapp, t("message_book"))}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="faq-whatsapp"
                            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-7 py-3 rounded-full text-sm font-medium transition"
                        >
                            <MessageCircle size={16} />
                            WhatsApp
                        </a>
                        <Link
                            to="/contact"
                            data-testid="faq-contact-link"
                            className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white hover:text-[#0F3A59] text-white px-7 py-3 rounded-full text-sm font-medium transition"
                        >
                            {t("nav_contact")}
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
